// src/pages/OrdersTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "../api/axios";
import OrderViewModal from "./modals/OrderViewModal";
import CreateContractModal from "./modals/CreateContractModal";
import ContractsModal from "./modals/ContractsModal";
import CreateOrderModal from "./modals/CreateOrderModal";
import "./OrdersTab.css";
import GenerateContractModal from "./modals/GenerateContractModal";

export default function OrdersTab({ locale, profile }) {
  const token = localStorage.getItem("token");
  const lang = (locale || "kz").startsWith("ru") ? "ru" : "kz";
  const langHeader = useMemo(
    () => ({ "Accept-Language": lang === "kz" ? "kk" : "ru" }),
    [lang]
  );

  // ===== i18n =====
  const t = {
    kz: {
      search: "Бұйрық атауы бойынша іздеу",
      orders: "Бұйрықтар",
      number: "Нөмір",
      status: "Күйі",
      created: "Құрылған",
      view: "Көру",
      contracts: "Келісімшарттар",
      createContract: "Келісімшарт жасау",
      empty: "Бұйрықтар жоқ",
      loadingError: "Мәліметтерді жүктеу қатесі",
      find: "Іздеу",
      createOrder: "Бұйрық жасау",
      perPage: "бетке:",
    },
    ru: {
      search: "Поиск по названию приказа",
      orders: "Приказы",
      number: "Номер",
      status: "Статус",
      created: "Создан",
      view: "Просмотр",
      contracts: "Договоры",
      createContract: "Создать договор",
      empty: "Приказов нет",
      loadingError: "Ошибка при загрузке данных",
      find: "Найти",
      createOrder: "Создать приказ",
      perPage: "на странице:",
    },
  }[lang];

  // ===== Статусы: нормализация + локализация =====
  const STATUS_I18N = {
     ru: {
     draft: "Черновик",
     ready: "Готово",
     signed: "Подписано",
     rejected: "Отклонено",
     pending: "Ожидается",
     signing: "Подготовка к подписи",
    },
    kz: {
      draft: "жоба",
      ready: "дайын",
      signed: "қол қойылған",
      rejected: "қабылданбаған",
      pending: "күтілуде",
      signing: "қол қоюға дайындау",
    },
  };

  // helper роли: internal_expert
const isInternalExpert = (p) => {
  if (!p || !p.role) return false;

  // если знаешь точный id роли → добавь его сюда
  const INTERNAL_ROLE_IDS = [3]; // у тебя в примере id: 3
  if (INTERNAL_ROLE_IDS.includes(Number(p.role.id))) return true;

  // подстрахуемся по текстовым полям роли
  const s = String(
    p.role.name || p.role.slug || p.role.title || ""
  ).trim().toLowerCase();

  return [
    "internal_expert",
    "ішкі сарапшы",
    "internal expert",
    "internal_expert",
    "internal-expert",
  ].includes(s);
};


  // классы для бейджей оставляем прежними (от нормализованного статуса)
  const KNOWN_STATUSES = new Set([
    "draft",
    "ready",
    "signed",
    "rejected",
    "pending",
    "signing",
  ]);

  function normStatus(s) {
    const v = String(s || "").trim().toLowerCase();
    if (KNOWN_STATUSES.has(v)) return v;
    // частые варианты от бэка/переводы
    if (["ожидается", "күтілуде", "waiting"].includes(v)) return "pending";
    if (["готов", "готово", "дайын", "ready"].includes(v)) return "ready";
    if (["подписано", "қол қойылған"].includes(v)) return "signed";
    if (["отклонено", "қабылданбаған"].includes(v)) return "rejected";
    if (["signing", "подготовка", "prepare"].includes(v)) return "signing";
    return "draft";
  }

  function statusLabel(s) {
    const key = normStatus(s);
    return STATUS_I18N[lang][key] || s || "—";
  }

  // ===== дата по языку =====
  const dateFmt = new Intl.DateTimeFormat(lang === "kz" ? "kk-KZ" : "ru-RU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

  // ===== state =====
  const [perPage, setPerPage] = useState(10);
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [newContractData, setNewContractData] = useState(null);

  // Modals
  const [orderDetails, setOrderDetails] = useState(null);
  const [contractsModalVisible, setContractsModalVisible] = useState(false);
  const [contractList, setContractList] = useState([]);
  const [contractApplications, setContractApplications] = useState([]);
  const [createContractVisible, setCreateContractVisible] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(null);

  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);

  const [contractExperts, setContractExperts] = useState([]);
  const [contractData, setContractData] = useState({
    external_expert_id: "",
    amount_numeric: "",
    amount_text_kz: "",
    amount_text_ru: "",
    baspa_tabaks: [""],
    durations: [""],
    page_counts: [""],
  });

  // ===== api =====
  const fetchOrders = async (p = 1) => {
    setLoading(true);
    try {
      const res = await axios.get("/orders/index", {
        headers: { Authorization: `Bearer ${token}`, ...langHeader },
        params: { page: p, q: q || undefined, per_page: perPage },
      });

      const payload = res.data?.data || res.data;
      const arr = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(res.data)
        ? res.data
        : [];

      setList(arr);

      const current = payload?.current_page ?? 1;
      const total = payload?.total ?? arr.length ?? 0;
      const pp = payload?.per_page ?? perPage;
      const last = payload?.last_page ?? (pp ? Math.ceil(total / pp) : 1);

      setPage(current);
      setLastPage(last);
      setPerPage(pp);
    } catch (e) {
      console.error(e);
      alert(t.loadingError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(1);
  }, [lang, perPage]); // при смене языка перегрузим

  useEffect(() => {
    fetchOrders(page);
  }, [page]);

  const openOrder = async (id) => {
    try {
      const res = await axios.get(`/orders/${id}`, {
        headers: { Authorization: `Bearer ${token}`, ...langHeader },
      });
      setOrderDetails(res.data);
    } catch (e) {
      console.error(e);
      alert(t.loadingError);
    }
  };

  const openContracts = async (orderId) => {
  if (!isInternalExpert(profile)) return; // защита на UI-уровне
  try {
    const res = await axios.get(`/orders/${orderId}/external-experts`, {
      headers: { Authorization: `Bearer ${token}`, ...langHeader },
    });
    const experts = res.data?.experts || [];
    const applications = Array.isArray(res.data?.applications)
      ? res.data.applications
      : [];

    setCurrentOrderId(orderId);
    setContractList(experts);
    setContractApplications(applications);
    setContractsModalVisible(true);
  } catch (e) {
    console.error(e);
    alert(t.loadingError || "Ошибка при загрузке экспертов");
  }
};


  const openCreateContract = async (orderId) => {
    try {
      const res = await axios.get(`/orders/${orderId}/applications`, {
        headers: { Authorization: `Bearer ${token}`, ...langHeader },
      });
      const applications = res.data?.applications || [];
      const experts = res.data?.external_experts || [];
      const initialArray = applications.map(() => "");
      setCurrentOrderId(orderId);
      setContractApplications(applications);
      setContractExperts(experts);
      setContractData({
        external_expert_id: "",
        amount_numeric: "",
        amount_text_kz: "",
        amount_text_ru: "",
        baspa_tabaks: [...initialArray],
        durations: [...initialArray],
        page_counts: [...initialArray],
      });
      setCreateContractVisible(true);
    } catch (e) {
      console.error(e);
      alert(t.loadingError);
    }
  };

  const handleCreateContract = async () => {
    try {
      const payload = {
        external_expert_id: Number(contractData.external_expert_id),
        amount_numeric: Number(contractData.amount_numeric),
        amount_text_kz: contractData.amount_text_kz,
        amount_text_ru: contractData.amount_text_ru,
        baspa_tabaks: contractData.baspa_tabaks.map(String),
        durations: contractData.durations.map(String),
        page_counts: contractData.page_counts.map(String),
      };
      await axios.post(`/orders/${currentOrderId}/generate-contract`, payload, {
        headers: { Authorization: `Bearer ${token}`, ...langHeader },
      });
      setCreateContractVisible(false);
      openContracts(currentOrderId);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || "Error");
    }
  };

  function buildPagination(current, total) {
    if (!total || total < 2) return [1];
    const candidates = new Set([
      1,
      2,
      total - 1,
      total,
      current - 1,
      current,
      current + 1,
    ]);
    const pages = [...candidates]
      .filter((n) => n >= 1 && n <= total)
      .sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < pages.length; i++) {
      const n = pages[i];
      const prev = pages[i - 1];
      if (i > 0 && n - prev > 1) out.push("...");
      out.push(n);
    }
    return out;
  }

  return (
    <div className="ot-wrap">
      <div className="ot-toolbar">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && fetchOrders(1)}
          placeholder={t.search}
          className="ot-input"
        />
        <button onClick={() => fetchOrders(1)} className="ot-btn ot-btn-primary">
          {t.find}
        </button>

        <div className="ot-spacer" />

        <label style={{ marginRight: 8, color: "#6b7280", fontSize: 14 }}>
          {t.perPage}
        </label>
        <select
          value={perPage}
          onChange={(e) => {
            setPerPage(Number(e.target.value));
            setPage(1);
          }}
          className="ot-input"
          style={{ width: 100, marginRight: 12 }}
        >
          <option value={2}>2</option>
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
        </select>

        <button
          className="ot-btn ot-btn-primary"
          onClick={() => setShowCreateOrderModal(true)}
        >
          {t.createOrder}
        </button>
      </div>

      <div className="ot-table-wrap">
        <table className="ot-table">
          <thead>
            <tr>
              <th>{t.orders}</th>
              <th>{t.number}</th>
              <th>{t.status}</th>
              <th>{t.created}</th>
              <th style={{ width: 260 }} />
            </tr>
          </thead>
          <tbody>
            {!loading && list.length === 0 && (
              <tr>
                <td colSpan={5} className="ot-empty">
                  {t.empty}
                </td>
              </tr>
            )}
            {list.map((o) => {
              const st = normStatus(o.status || "draft");
              return (
                <tr key={o.id}>
                  <td>
                    <div className="ot-title">{o.title}</div>
                  </td>
                  <td>{o.order_number || "—"}</td>
                  <td>
                    <span className={`ot-badge ot-status-${st}`}>
                      {statusLabel(st)}
                    </span>
                  </td>
                  <td>{o.created_at ? dateFmt.format(new Date(o.created_at)) : "—"}</td>
                  <td className="ot-actions">
  <button
    className="ot-btn ot-btn-secondary"
    onClick={() => openOrder(o.id)}
  >
    {t.view}
  </button>

  {isInternalExpert(profile) && (
    <button
      className="ot-btn ot-btn-secondary"
      onClick={() => openContracts(o.id)}
    >
      {t.contracts}
    </button>
  )}

  {isInternalExpert(profile) && st === "ready" && (
    <button
      className="ot-btn ot-btn-primary"
      onClick={() => openCreateContract(o.id)}
    >
      {t.createContract}
    </button>
  )}
</td>

                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {lastPage > 1 && (
        <div className="ot-pagination">
          <button
            className="ot-page-btn"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          >
            ‹
          </button>

          {buildPagination(page, lastPage).map((item, idx) =>
            item === "..." ? (
              <span key={`ellipsis-${idx}`} className="ot-ellipsis">
                …
              </span>
            ) : (
              <button
                key={item}
                className={`ot-page-btn ${item === page ? "is-active" : ""}`}
                onClick={() => setPage(item)}
                aria-current={item === page ? "page" : undefined}
              >
                {item}
              </button>
            )
          )}

          <button
            className="ot-page-btn"
            disabled={page === lastPage}
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}

      {/* модалки */}
      {orderDetails && (
        <OrderViewModal
          orderDetails={orderDetails}
          onClose={() => setOrderDetails(null)}
          locale={lang}
          profile={profile}
          token={token}
          fetchOrderDetails={(id) => openOrder(id)}
          fetchGroups={() => fetchOrders(page)}
          orderForm={{}}
          setOrderForm={() => {}}
        />
      )}

      {contractsModalVisible && (
        <ContractsModal
          locale={lang}
          contractList={contractList}
          applications={contractApplications}
          orderId={currentOrderId}
          onClose={() => setContractsModalVisible(false)}
          onCreate={({ expert, orderId, applications }) => {
            setContractsModalVisible(false);
            setIsGenerateOpen(true);
            setNewContractData({
              orderId,
              externalExpertId: expert.id,
              expertName: expert.name,
              applications,
            });
          }}
        />
      )}

      {createContractVisible && (
        <CreateContractModal
          locale={lang}
          contractExperts={contractExperts}
          contractApplications={contractApplications}
          contractData={contractData}
          setContractData={setContractData}
          onSubmit={handleCreateContract}
          onClose={() => setCreateContractVisible(false)}
        />
      )}

      {showCreateOrderModal && (
        <CreateOrderModal
          locale={lang}
          token={token}
          onClose={() => {
            setShowCreateOrderModal(false);
            fetchOrders(page);
          }}
        />
      )}

      {isGenerateOpen && newContractData && (
        <GenerateContractModal
          isOpen={isGenerateOpen}
          onClose={() => setIsGenerateOpen(false)}
          token={token}
          locale={lang}
          orderId={newContractData.orderId}
          externalExpertId={newContractData.externalExpertId}
          expertName={newContractData.expertName}
          applications={newContractData.applications}
          onCreated={() => fetchOrders(page)}
        />
      )}
    </div>
  );
}
